/**
 * OverbookDialog
 * ──────────────────────────────────────────────────────────────────────
 * Real-mode-only modal that drives the A24/A25 overbooking flow:
 *   1. idle      → user fills patient, visit type, start, reason
 *   2. previewing → POST /overbooking/preview (allowed 200 / 403 / 409 / 422)
 *   3. confirm   → preview returned 200; "Confirm overbook" enabled
 *   4. booking   → POST /overbooking/book → 201 closes with toast
 *   5. error     → reason_codes shown inline; user can edit + re-preview
 *
 * Closed-form state machine — no implicit transitions; every move is
 * driven by an explicit button click. Keeps the test surface tiny.
 *
 * Decision-code → user copy table (matches backend `_evaluate_overbook`):
 *   ROLE_NOT_ALLOWED        → 403 "Your role is not authorized to overbook."
 *   REASON_REQUIRED         → 422 "A reason is required to overbook."
 *   OVERBOOK_DISABLED       → 409 "Overbooking is currently disabled in policy."
 *   OVERBOOK_CAP_EXCEEDED   → 409 "Overbook cap reached for this provider/day."
 *   <unknown / 5xx>         → "Could not verify overbook — please retry."
 *
 * The dialog is fully controlled — `open` + `onOpenChange` come from the
 * parent. After a successful book we call `onBooked(appointment)` so the
 * calendar can refresh.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { AlertTriangle, Loader2, ShieldAlert, CheckCircle2 } from 'lucide-react';
import * as schedulerApi from '../../../../lib/api/schedulerApi';
import { toast } from 'sonner';

/* ── Pure helpers (also exported for unit testing) ── */

export const REASON_CODE_COPY = {
  ROLE_NOT_ALLOWED:      'Your role is not authorized to overbook.',
  REASON_REQUIRED:       'A reason is required to overbook.',
  OVERBOOK_DISABLED:     'Overbooking is currently disabled in policy.',
  OVERBOOK_CAP_EXCEEDED: 'Overbook cap reached for this provider/day.',
};

/**
 * Map an httpClient error envelope to a normalized UX state. Pure — no
 * side-effects — so the same logic powers both the dialog and the
 * release-button error toast.
 *   { status, code, message, details } → { kind, message, reasonCodes }
 */
export const mapOverbookError = (error) => {
  if (!error) {
    return { kind: 'unknown', message: 'Unknown error', reasonCodes: [] };
  }
  const status = error.status || 0;
  // Backend may nest the preview envelope under `details.detail` (FastAPI
  // wraps HTTPException(detail={...}) inside `details: { detail: {...} }`)
  // or directly under `details` depending on the route. Walk both.
  const detail = (error.details && (error.details.detail || error.details)) || {};
  const reasonCodes = Array.isArray(detail.reason_codes) ? detail.reason_codes : [];
  if (status === 403) {
    return {
      kind: 'forbidden',
      message: REASON_CODE_COPY.ROLE_NOT_ALLOWED,
      reasonCodes,
    };
  }
  if (status === 422) {
    return {
      kind: 'validation',
      message: REASON_CODE_COPY.REASON_REQUIRED,
      reasonCodes,
    };
  }
  if (status === 409) {
    const code = reasonCodes.find((c) => REASON_CODE_COPY[c]);
    return {
      kind: 'conflict',
      message: REASON_CODE_COPY[code] || 'Cannot overbook this slot right now.',
      reasonCodes,
    };
  }
  if (status === 404) {
    return { kind: 'notfound', message: 'Provider or appointment not found.', reasonCodes };
  }
  if (status >= 500 || status === 0) {
    return {
      kind: 'server',
      message: 'Could not verify overbook — please retry.',
      reasonCodes,
    };
  }
  return {
    kind: 'unknown',
    message: error.message || 'Unknown error',
    reasonCodes,
  };
};

const OverbookDialog = ({
  open,
  onOpenChange,
  prefillEntityId = '',
  prefillStart = '',
  prefillLocationId = '',
  visitTypes = [],
  patients = [],
  onBooked,
}) => {
  const [stage, setStage] = useState('idle');           // idle|previewing|confirm|booking|error
  const [patientId, setPatientId] = useState('');
  const [entityId, setEntityId] = useState(prefillEntityId);
  const [start, setStart] = useState(prefillStart);
  const [visitTypeId, setVisitTypeId] = useState('');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [errorState, setErrorState] = useState(null);

  // Reset every time the dialog opens with new prefill data.
  useEffect(() => {
    if (open) {
      setStage('idle');
      setPatientId('');
      setEntityId(prefillEntityId);
      setStart(prefillStart);
      setVisitTypeId('');
      setReason('');
      setPreview(null);
      setErrorState(null);
    }
  }, [open, prefillEntityId, prefillStart]);

  const canPreview = useMemo(() => (
    !!entityId && !!start && !!reason.trim() && stage !== 'previewing' && stage !== 'booking'
  ), [entityId, start, reason, stage]);

  const canConfirm = useMemo(() => (
    stage === 'confirm' && !!patientId && !!preview?.allowed
  ), [stage, patientId, preview]);

  const handlePreview = useCallback(async () => {
    setStage('previewing');
    setErrorState(null);
    const res = await schedulerApi.previewOverbooking({
      entity_id:     entityId,
      start,
      location_id:   prefillLocationId || undefined,
      visit_type_id: visitTypeId || undefined,
      reason:        reason.trim(),
    });
    if (res.ok) {
      setPreview(res.data);
      setStage('confirm');
    } else {
      const mapped = mapOverbookError(res.error);
      setErrorState(mapped);
      setStage('error');
    }
  }, [entityId, start, prefillLocationId, visitTypeId, reason]);

  const handleConfirm = useCallback(async () => {
    if (!canConfirm) return;
    setStage('booking');
    setErrorState(null);
    const res = await schedulerApi.overbookAppointment({
      patient_id:    patientId,
      entity_id:     entityId,
      start,
      location_id:   prefillLocationId || undefined,
      visit_type_id: visitTypeId || undefined,
      reason:        reason.trim(),
    });
    if (res.ok) {
      toast.success('Overbooked appointment created.');
      onBooked && onBooked(res.data?.appointment);
      onOpenChange(false);
    } else {
      const mapped = mapOverbookError(res.error);
      setErrorState(mapped);
      setStage('error');
      toast.error(mapped.message);
    }
  }, [canConfirm, patientId, entityId, start, prefillLocationId, visitTypeId, reason, onBooked, onOpenChange]);

  const reEdit = useCallback(() => {
    setStage('idle');
    setErrorState(null);
    setPreview(null);
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        data-testid="overbook-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-amber-500" />
            Overbook this slot
          </DialogTitle>
          <DialogDescription>
            Bypasses the standard cap. Action is logged in the audit trail.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {/* Patient picker — uses provided list or free text */}
          <div>
            <label className="block text-[11px] font-medium text-slate-600 mb-1">Patient</label>
            {patients.length > 0 ? (
              <select
                className="w-full text-sm border border-slate-200 rounded-md p-1.5"
                value={patientId}
                onChange={(e) => setPatientId(e.target.value)}
                data-testid="overbook-dialog-patient-select"
              >
                <option value="">Select patient…</option>
                {patients.map((p) => (
                  <option key={p.id} value={p.id}>{p.name || p.id}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                className="w-full text-sm border border-slate-200 rounded-md p-1.5"
                placeholder="Patient ID"
                value={patientId}
                onChange={(e) => setPatientId(e.target.value)}
                data-testid="overbook-dialog-patient-input"
              />
            )}
          </div>

          {/* Visit type */}
          <div>
            <label className="block text-[11px] font-medium text-slate-600 mb-1">Visit type</label>
            <select
              className="w-full text-sm border border-slate-200 rounded-md p-1.5"
              value={visitTypeId}
              onChange={(e) => setVisitTypeId(e.target.value)}
              data-testid="overbook-dialog-visit-type"
            >
              <option value="">Default duration (30 min)</option>
              {visitTypes.map((v) => (
                <option key={v.id} value={v.id}>{v.name || v.label}</option>
              ))}
            </select>
          </div>

          {/* Reason — required */}
          <div>
            <label className="block text-[11px] font-medium text-slate-600 mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              className="w-full text-sm border border-slate-200 rounded-md p-1.5 min-h-[60px]"
              placeholder="e.g. ER walk-in, urgent triage…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="overbook-dialog-reason"
              disabled={stage === 'booking'}
            />
          </div>

          {/* Preview result */}
          {stage === 'confirm' && preview?.preview && (
            <div
              className="border border-emerald-200 bg-emerald-50 rounded-md p-2"
              data-testid="overbook-dialog-preview-ok"
            >
              <div className="flex items-center gap-1.5 text-emerald-700 text-[11px] font-medium">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Allowed by policy
              </div>
              <div className="mt-1 text-[11px] text-slate-600">
                Booked {preview.preview.current_booked} / {preview.preview.current_capacity}
                {' · '}Overbook headroom {preview.preview.overbook_headroom}
              </div>
            </div>
          )}

          {/* Error / decision-code panel */}
          {stage === 'error' && errorState && (
            <div
              className="border border-red-200 bg-red-50 rounded-md p-2 space-y-1"
              data-testid="overbook-dialog-error"
            >
              <div className="flex items-center gap-1.5 text-red-700 text-[11px] font-medium">
                <AlertTriangle className="h-3.5 w-3.5" />
                {errorState.message}
              </div>
              {errorState.reasonCodes.length > 0 && (
                <div className="flex flex-wrap gap-1" data-testid="overbook-dialog-reason-codes">
                  {errorState.reasonCodes.map((c) => (
                    <Badge key={c} variant="outline" className="text-[9px] border-red-200 text-red-700">
                      {c}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="overbook-dialog-cancel"
          >
            Cancel
          </Button>
          {stage === 'error' ? (
            <Button onClick={reEdit} data-testid="overbook-dialog-edit">
              Edit & retry
            </Button>
          ) : stage === 'confirm' ? (
            <Button
              onClick={handleConfirm}
              disabled={!canConfirm}
              data-testid="overbook-dialog-confirm"
            >
              {stage === 'booking' ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Confirm overbook
            </Button>
          ) : (
            <Button
              onClick={handlePreview}
              disabled={!canPreview}
              data-testid="overbook-dialog-preview"
            >
              {stage === 'previewing' ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Preview
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OverbookDialog;
